import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { Box, Typography, Paper, Alert, CircularProgress, Chip } from '@mui/material';
import { MdAutoAwesome } from 'react-icons/md';
import AdminInvalidateInsightCacheButton from './AdminInvalidateInsightCacheButton';

const INSIGHT_CARD = {
  bg: '#F3F0FF',
  border: '#D6BCFA',
  text: '#4A438C',
  iconBg: '#5A52BD',
  emptyBodyText: 'No insights available for this view yet.',
};

/** Live Bedrock — note "current" (dev signal for fresh AI run). */
const AI_ATTRIBUTION_LIVE =
  'Generated by AI from your current operational data. Verify important decisions against Operations and session records.';

/** Cached insight — no "current" (dev signal for Mongo/Bedrock cache hit). */
const AI_ATTRIBUTION_CACHED =
  'Generated by AI from your operational data. Verify important decisions against Operations and session records.';

const AI_DISCLAIMER_SHORT = 'This insight was generated by AI.';

function NarrativeWithBold({ text, sx }) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <Typography component="div" sx={sx}>
      {parts.map((part, i) => {
        const m = part.match(/^\*\*([^*]+)\*\*$/);
        if (m) return <strong key={i}>{m[1]}</strong>;
        return part;
      })}
    </Typography>
  );
}

/**
 * Lavender insight card (Operations / Overview executive summary).
 * @param {{
 *   title: string,
 *   subtitle?: string,
 *   insightsFromView?: { points?: string[], narrative?: string, error?: string, fromCache?: boolean } | null,
 *   legacyInsight?: string | null,
 *   source?: 'ai' | 'template',
 *   loading?: boolean,
 *   disclaimerMode?: 'long' | 'short',
 *   attributionLive?: string,
 *   attributionCached?: string,
 *   showFreshnessChip?: boolean, // adminView only — defaults to true when source is ai
 *   emptyBodyText?: string,
 *   adminCacheInvalidation?: {
 *     surface: string,
 *     cacheParams: Record<string, string | undefined>,
 *     onInvalidated?: () => void | Promise<void>,
 *   },
 *   sx?: object,
 * }} props
 */
function formatInsightFreshnessSubtitle(insightsFromView) {
  if (!insightsFromView) return null;
  const fromCache = Boolean(insightsFromView.fromCache);
  if (fromCache && insightsFromView.generatedAt) {
    try {
      const when = new Date(insightsFromView.generatedAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      const modeLabel =
        insightsFromView.cacheMode === 'immutable' ? 'Cached permanently' : 'Cached';
      return `${modeLabel} · generated ${when}`;
    } catch {
      return insightsFromView.cacheMode === 'immutable' ? 'Cached permanently' : 'Cached insight';
    }
  }
  if (!fromCache) return 'Live · generated from current data';
  return 'Cached insight';
}

export default function DashboardInsightCard({
  title,
  subtitle,
  insightsFromView,
  legacyInsight,
  source = 'template',
  loading = false,
  disclaimerMode = 'long',
  attributionLive = AI_ATTRIBUTION_LIVE,
  attributionCached = AI_ATTRIBUTION_CACHED,
  showFreshnessChip,
  emptyBodyText,
  adminCacheInvalidation,
  sx = {},
}) {
  const { adminView } = useOutletContext() || {};
  const effectiveShowFreshness = showFreshnessChip ?? source === 'ai';
  const showAdminCacheMeta = Boolean(adminView && effectiveShowFreshness);
  const resolvedEmptyBodyText = emptyBodyText ?? INSIGHT_CARD.emptyBodyText;
  const insightError =
    insightsFromView && typeof insightsFromView.error === 'string' && insightsFromView.error.trim()
      ? insightsFromView.error.trim()
      : '';
  const insightPoints = insightsFromView?.points;
  const insightHasPoints = Array.isArray(insightPoints) && insightPoints.length > 0;
  const insightNarrativeRaw = insightsFromView?.narrative;
  const insightNarrative =
    typeof insightNarrativeRaw === 'string' && insightNarrativeRaw.trim()
      ? insightNarrativeRaw.trim()
      : '';
  const insightLegacy =
    typeof legacyInsight === 'string' && legacyInsight.trim() ? legacyInsight.trim() : '';
  const showAiAttribution = source === 'ai' && !loading;
  const aiAttributionText =
    showAiAttribution && insightsFromView?.fromCache ? attributionCached : attributionLive;
  const useShortDisclaimer = disclaimerMode === 'short';
  const freshnessSubtitle =
    showAiAttribution && showAdminCacheMeta ? formatInsightFreshnessSubtitle(insightsFromView) : null;
  const freshnessLabel = insightsFromView?.fromCache ? 'Cached' : 'Live';

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 3,
        borderRadius: '10px',
        bgcolor: INSIGHT_CARD.bg,
        border: `1px solid ${INSIGHT_CARD.border}`,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: INSIGHT_CARD.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 0.25,
          }}
          aria-hidden
        >
          <MdAutoAwesome size={22} color="#fff" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mb: subtitle ? 0.25 : showAiAttribution && useShortDisclaimer ? 0.5 : 1,
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: INSIGHT_CARD.text,
                lineHeight: 1.3,
                m: 0,
                flex: adminCacheInvalidation ? 1 : undefined,
                minWidth: 0,
              }}
            >
              {title}
            </Typography>
            {adminCacheInvalidation ? (
              <AdminInvalidateInsightCacheButton
                surface={adminCacheInvalidation.surface}
                cacheParams={adminCacheInvalidation.cacheParams}
                onInvalidated={adminCacheInvalidation.onInvalidated}
                disabled={loading}
              />
            ) : null}
            {showAiAttribution ? (
              <Chip
                icon={<MdAutoAwesome size={14} />}
                label="AI-generated"
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  color: INSIGHT_CARD.text,
                  borderColor: INSIGHT_CARD.border,
                  bgcolor: 'rgba(90, 82, 189, 0.08)',
                  '& .MuiChip-icon': { color: INSIGHT_CARD.iconBg, ml: 0.75 },
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ) : null}
            {showAiAttribution && showAdminCacheMeta ? (
              <Chip
                label={freshnessLabel}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  color: insightsFromView?.fromCache ? '#6b7280' : '#14532d',
                  bgcolor: insightsFromView?.fromCache ? 'rgba(107, 114, 128, 0.12)' : 'rgba(5, 150, 105, 0.12)',
                  border: insightsFromView?.fromCache
                    ? '1px solid rgba(107, 114, 128, 0.25)'
                    : '1px solid rgba(5, 150, 105, 0.25)',
                }}
              />
            ) : null}
          </Box>
          {subtitle ? (
            <Typography sx={{ fontSize: 13, color: INSIGHT_CARD.text, opacity: 0.85, mb: showAiAttribution ? 0.5 : 1 }}>
              {subtitle}
            </Typography>
          ) : null}
          {freshnessSubtitle ? (
            <Typography sx={{ fontSize: 12, color: INSIGHT_CARD.text, opacity: 0.7, mb: showAiAttribution ? 0.5 : 1 }}>
              {freshnessSubtitle}
            </Typography>
          ) : null}
          {showAiAttribution && useShortDisclaimer ? (
            <>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: INSIGHT_CARD.text,
                  lineHeight: 1.5,
                  mb: 0.5,
                }}
              >
                {AI_DISCLAIMER_SHORT}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: INSIGHT_CARD.text,
                  opacity: 0.75,
                  lineHeight: 1.5,
                  mb: 1,
                }}
              >
                {aiAttributionText}
              </Typography>
            </>
          ) : null}
          {showAiAttribution && !useShortDisclaimer ? (
            <Typography
              sx={{
                fontSize: 13,
                color: INSIGHT_CARD.text,
                opacity: 0.75,
                lineHeight: 1.5,
                mb: 1,
              }}
            >
              {aiAttributionText}
            </Typography>
          ) : null}
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={20} sx={{ color: INSIGHT_CARD.iconBg }} />
              <Typography sx={{ fontSize: '0.875rem', color: INSIGHT_CARD.text }}>
                Generating summary…
              </Typography>
            </Box>
          ) : (
            <>
              {insightError ? (
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{
                    mb: insightHasPoints || insightNarrative || insightLegacy ? 1.5 : 0,
                    py: 0.5,
                    fontSize: '0.8125rem',
                    alignItems: 'flex-start',
                    borderColor: INSIGHT_CARD.border,
                    bgcolor: 'rgba(90, 82, 189, 0.06)',
                    color: INSIGHT_CARD.text,
                    '& .MuiAlert-icon': { color: INSIGHT_CARD.iconBg },
                  }}
                >
                  {insightError}
                </Alert>
              ) : null}
              {insightHasPoints ? (
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.25,
                    color: INSIGHT_CARD.text,
                    '& li': {
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      lineHeight: 1.65,
                      mb: 0.75,
                      pl: 0.25,
                    },
                    '& li:last-of-type': { mb: 0 },
                    '& b': { fontWeight: 700 },
                  }}
                >
                  {insightPoints.map((html, idx) => (
                    <Box component="li" key={idx} dangerouslySetInnerHTML={{ __html: html }} />
                  ))}
                </Box>
              ) : insightNarrative ? (
                <NarrativeWithBold
                  text={insightNarrative}
                  sx={{ fontSize: '0.875rem', fontWeight: 400, color: INSIGHT_CARD.text, lineHeight: 1.65 }}
                />
              ) : insightLegacy ? (
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 400, color: INSIGHT_CARD.text, lineHeight: 1.65 }}>
                  {insightLegacy}
                </Typography>
              ) : insightError ? null : (
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 400, color: INSIGHT_CARD.text, lineHeight: 1.65 }}>
                  {resolvedEmptyBodyText}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
